import { useState } from 'react';

import { Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { CityOption } from '@/lib/pages/forms/helpers';

interface CitySelectionDialogProps {
  open: boolean;
  cityOptions: CityOption[];
  onSelect: (city: string) => void;
  onCancel: () => void;
}

export const CitySelectionDialog = ({
  open,
  cityOptions,
  onSelect,
  onCancel,
}: CitySelectionDialogProps) => {
  const [selectedCity, setSelectedCity] = useState<string>(
    cityOptions[0]?.value ?? ''
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select City</DialogTitle>
          <DialogDescription>
            Multiple city options were found for this address. Please select the
            correct one.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <Info className="shrink-0 text-blue-600" size={16} />
          <span className="text-xs text-blue-800">
            The first option is pre-selected. You can change it or click Confirm
            to continue.
          </span>
        </div>
        <RadioGroup value={selectedCity} onValueChange={setSelectedCity}>
          {cityOptions.map((option, index) => {
            const uniqueId = `${option.type}-${index}`;
            return (
              <div key={uniqueId} className="mb-1 flex items-center space-x-2">
                <RadioGroupItem value={option.value} id={uniqueId} />
                <Label htmlFor={uniqueId} className="cursor-pointer">
                  {option.value}
                </Label>
              </div>
            );
          })}
        </RadioGroup>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Use Default
          </Button>
          <Button onClick={() => onSelect(selectedCity)}>
            Confirm Selection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
